import React, { useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { cn } from './ui/utils';
import { useMarkets } from '@/hooks/useMarkets';
import { getCountryName } from '@/data/countries';
import { OTHER_REGION, findMarket, groupMarketsByRegion, joinMarkets, marketMatches, splitMarkets } from '@/utils/markets';

export interface MarketPickerProps {
  /**
   * The stored value: a market name ("Sweden") by default, a code ("SE") with valueKind="code". With `multiple`, several
   * joined ("Sweden, Norway"; older "Sweden + Norway" reads too).
   */
  value: string;
  onChange: (value: string) => void;
  /** What value and onChange carry. The cowork screens store names, so names are the default. */
  valueKind?: 'name' | 'code';
  /** Several markets in one value (joined with ", "). */
  multiple?: boolean;
  /**
   * Names the server offers that are not markets of the list (e.g. "Nordics"). Shown under "Other" so nothing a screen
   * offered before disappears. Names that are list markets are not repeated.
   */
  extra?: readonly string[];
  /**
   * Offer only these markets of the list (codes), e.g. the markets a provider covers. A stored value outside them still
   * shows (under Other) and is kept.
   */
  onlyCodes?: readonly string[];
  /** Shown when nothing is picked: "Pick a market" (single) or "Pick markets" (multiple) unless given. */
  placeholder?: string;
  /** Accessible name of the trigger, e.g. "Market". */
  label?: string;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

/** One row of the list. `value` is what gets stored; a market of the list matches stored parts by code, alias or name. */
interface Row { key: string; code: string; label: string; region: string; value: string }

/**
 * One market picker for Omni (support #238): every market of the Beatly list, grouped by region in the list's order
 * (Nordics, Rest of Europe, North America, LatAm, APAC), type to filter by name or code (accents ignored).
 *
 * Stored values the list does not know (an old "UK", "Nordics", a code outside the list) still show, under Other, and
 * are saved back exactly as written: the picker only calls onChange when someone picks, and keeps every other part as is.
 */
export const MarketPicker: React.FC<MarketPickerProps> = ({
  value, onChange, valueKind = 'name', multiple = false, extra = [], onlyCodes, placeholder, label = 'Market', disabled = false, className, ...rest
}) => {
  const emptyText = placeholder ?? (multiple ? 'Pick markets' : 'Pick a market');
  const markets = useMarkets();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const parts = useMemo(
    () => (multiple ? splitMarkets(value, markets.countries) : value.trim() ? [value.trim()] : []),
    [multiple, value, markets.countries],
  );
  /** Stored part → the list row it stands for (code), or the part itself when the list does not know it. */
  const keyOfPart = (p: string) => findMarket(p, markets.countries)?.code ?? `other:${p}`;
  const pickedKeys = new Set(parts.map(keyOfPart));

  const rows: Row[] = useMemo(() => {
    const only = onlyCodes ? new Set(onlyCodes.map((c) => c.toUpperCase())) : null;
    const list: Row[] = markets.countries.filter((m) => !only || only.has(m.code)).map((m) => ({ key: m.code, code: m.code, label: m.label, region: m.region, value: valueKind === 'code' ? m.code : m.label }));
    const seen = new Set<string>();
    const listed = new Set(list.map((r) => r.key));
    for (const name of [...extra, ...parts]) {
      const n = name.trim();
      const known = findMarket(n, markets.countries);
      if (!n || seen.has(n) || (known && listed.has(known.code))) continue;
      seen.add(n);
      const shownAs = valueKind === 'code' && /^[A-Za-z]{2}$/.test(n) ? getCountryName(n.toUpperCase()) : n;
      if (known) list.push({ key: known.code, code: known.code, label: known.label, region: OTHER_REGION, value: n });
      else list.push({ key: `other:${n}`, code: '', label: shownAs, region: OTHER_REGION, value: n });
    }
    return list;
  }, [markets.countries, extra, parts, valueKind, onlyCodes]);

  const labelOfPart = (p: string) => rows.find((r) => r.key === keyOfPart(p))?.label ?? p;
  const shown = parts.map(labelOfPart).join(', ');
  const groups = groupMarketsByRegion(rows.filter((r) => marketMatches(r, query)), markets.regions);

  const pick = (r: Row) => {
    if (!multiple) {
      onChange(r.value);
      setOpen(false);
      setQuery('');
      return;
    }
    const next = pickedKeys.has(r.key) ? parts.filter((p) => keyOfPart(p) !== r.key) : [...parts, r.value];
    onChange(joinMarkets(next));
  };

  return (
    <Popover open={open} onOpenChange={(o) => { if (disabled) return; setOpen(o); if (!o) setQuery(''); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={`${label}: ${shown || emptyText}`}
          data-testid={rest['data-testid'] ?? 'market-picker'}
          className={cn('inline-flex items-center justify-between gap-2 rounded-lg border border-[#d0d5dd] bg-white px-2.5 py-2 text-left text-[13px] text-[#344054] disabled:opacity-50', className)}
        >
          <span className={cn('truncate', !shown && 'text-[#98a2b3]')}>{shown || emptyText}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[#98a2b3]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search market or code..." value={query} onValueChange={setQuery} />
          <CommandList className="max-h-80">
            <CommandEmpty>No market found.</CommandEmpty>
            {groups.map((g) => (
              <CommandGroup key={g.region} heading={g.region}>
                {g.markets.map((r) => (
                  <CommandItem key={r.key} value={r.key} onSelect={() => pick(r)} aria-checked={pickedKeys.has(r.key)} data-picked={pickedKeys.has(r.key) || undefined}>
                    <Check aria-hidden className={cn('h-3.5 w-3.5', pickedKeys.has(r.key) ? 'opacity-100 text-[#be43ff]' : 'opacity-0')} />
                    <span className="flex-1">{r.label}{pickedKeys.has(r.key) && <span className="sr-only"> (selected)</span>}</span>
                    {r.code && <span className="text-xs text-[#98a2b3]">{r.code}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
